import * as React from 'react'
import clsx from 'clsx'
import * as XState from '@xstate/react'
import { useFieldArray } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import Machine from './machine'
import NetworkMachine from 'machines/Network'
import * as Buttons from 'components/button'
import * as Icons from 'components/icons'
import * as Colors from 'constants/colors'
import * as Figures from 'layouts/products/min/Figure'
import * as SubmissionClient from 'clients/min/submission'
import * as Hooks from 'hooks'
import Layout from '../../components/controls/Layout'
import texts from 'components/text/Text.module.css'
import figures from 'layouts/products/min/Figure.module.css'
import * as Anchors from '../../../screens/Anchors'
import { omit } from '@avcan/utils/object'
import { useUISchema } from '../../hooks'
import styles from './styles.module.css'

export default function Images({ props, options }) {
    const { name, register } = props
    const intl = useIntl()
    const { widget } = useUISchema(name)
    const { description, label } = widget
    const [refFullscreen, enterFullscreen] = Hooks.useFullscreen()
    const [network] = XState.useMachine(NetworkMachine)
    const online = network.matches('online')
    const tooltip = intl.formatMessage({
        defaultMessage: 'Manage images in fullscreen mode',
        description: 'MIN Form / Images widget',
    })
    let status = null

    if (network.matches('offline')) {
        status = (
            <span className={texts.Warning}>
                <FormattedMessage
                    description="MIN Form / Images widget"
                    defaultMessage="Make sure to be online to upload images."
                />
            </span>
        )
    }

    return (
        <Provider name={name} register={register} options={options}>
            <Layout
                description={online && description}
                label={status || label}
                name={name}
                className={styles.Layout}>
                <Buttons.Button
                    kind={Buttons.INCOGNITO}
                    data-tooltip={tooltip}
                    data-tooltip-placement="left"
                    onClick={enterFullscreen}>
                    <Icons.Fullscreen />
                </Buttons.Button>
                <div className={styles.Wrapper} ref={refFullscreen}>
                    <ImageSet />
                    {online && <TransientImageSet />}
                    {online && <AddImage />}
                    {online && <DropZone />}
                </div>
            </Layout>
            <NetworkConnectionStatus />
        </Provider>
    )
}

// Util components
function NetworkConnectionStatus() {
    const [network] = XState.useMachine(NetworkMachine)

    if (network.matches('online.slow') || network.matches('online.medium')) {
        return (
            <p className={texts.Warning}>
                <FormattedMessage
                    description="MIN Form / Images widget"
                    defaultMessage="Your connection is slow. You might experience some issues while uploading images."
                />
            </p>
        )
    }

    return null
}
function TransientImageSet() {
    const [state] = useService()

    return state.context.processes.map(process => (
        <TransientImage key={process.id} process={process} />
    ))
}

function TransientImage({ process }) {
    const intl = useIntl()
    const [state, send] = XState.useActor(process)
    const { url, image, retries } = state.context
    const { filename } = image
    const removeTooltip = intl.formatMessage({
        description: 'MIN Form / Images widget',
        defaultMessage: 'Remove upload',
    })
    function remove() {
        send('REMOVE')
    }

    return (
        <section className={styles.Image}>
            <header style={{ visibility: retries > 2 ? 'visible' : 'hidden' }}>
                <span />
                <div className={styles.Actions}>
                    <Buttons.Button onClick={remove} data-tooltip={removeTooltip}>
                        <Icons.Remove color={Colors.WHITE} />
                    </Buttons.Button>
                </div>
            </header>
            <figure className={figures.Figure}>
                <div className={styles.TransientImage}>
                    <output>
                        <img key={url} src={url} alt={filename} title={filename} />
                    </output>
                    <label>
                        <TransientImageMessage process={process} />
                        <TransientImageRetry process={process} retries={retries} />
                        <progress />
                    </label>
                </div>
                <figcaption>
                    <textarea disabled />
                </figcaption>
            </figure>
        </section>
    )
}

function TransientImageRetry({ process, retries }) {
    const [state, send] = XState.useActor(process)
    const intl = useIntl()
    const subject = intl.formatMessage({
        description: 'Form - Image Reading/Uploading Retry message',
        defaultMessage: 'Mountain Information Network - Upload image not working',
    })
    function retry() {
        send('RETRY')
    }

    if (!state.nextEvents.includes('RETRY')) {
        return null
    }

    return (
        <Buttons.ButtonSet>
            <Buttons.Button onClick={retry}>
                <FormattedMessage
                    description="Form - Image Reading/Uploading Retry message"
                    defaultMessage="Try again"
                />
            </Buttons.Button>
            {retries > 2 && (
                <Anchors.ContactUs
                    subject={subject}
                    body={JSON.stringify(omit(state.context, 'url'))}
                />
            )}
        </Buttons.ButtonSet>
    )
}

function TransientImageMessage({ process }) {
    const [state] = XState.useActor(process)

    switch (true) {
        case state.matches('read.failure'):
            return (
                <div className={texts.Error}>
                    <FormattedMessage
                        description="Mountain Information Network - Form - Image Reading/Uploading Message"
                        defaultMessage="Error happened while reading file."
                    />
                </div>
            )
        case state.matches('read'):
            return (
                <div>
                    <FormattedMessage
                        description="Mountain Information Network - Form - Image Reading/Uploading Message"
                        defaultMessage="Reading file..."
                    />
                </div>
            )
        case state.matches('upload.failure'):
            return (
                <div className={texts.Error}>
                    <FormattedMessage
                        description="Mountain Information Network - Form - Image Reading/Uploading Message"
                        defaultMessage="Error happened while uploading file."
                    />
                </div>
            )
        case state.matches('upload'):
            return (
                <div>
                    <FormattedMessage
                        description="Mountain Information Network - Form - Image Reading/Uploading Message"
                        defaultMessage="Uploading image..."
                    />
                </div>
            )
        default:
            return null
    }
}

function ImageSet() {
    const { fields } = useContext()

    return fields.map((_, index) => <Image key={index} index={index} />)
}

function Image({ index }) {
    const { name, register, fields } = useContext()
    const intl = useIntl()
    const count = index + 1
    const total = fields.length
    const alt = intl.formatMessage(
        {
            description: 'MIN Form / Image Upload Widget',
            defaultMessage: 'Mountain Information Network image #{index}',
        },
        { index: count }
    )
    const placeholder = intl.formatMessage({
        description: 'MIN Form / Image Upload Widget',
        defaultMessage: 'Write some useful information about your picture',
    })
    const { caption, url, id, width, height } = fields[index]
    const namePrefix = `${name}[${index}].`

    // ///////////////////////////////////////////////////////////////////////////////////////////////////
    // In field array, "register" needs to be called
    // More details: https://react-hook-form.com/api/#useFieldArray
    // ///////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <section className={styles.Image}>
            <header>
                <span>
                    {count} / {total}
                </span>
                <Actions index={index} lastIndex={total - 1} />
            </header>
            <Figures.Figure>
                <Figures.Image url={url} alt={alt} width={width} height={height} />
                <Figures.Caption>
                    <textarea
                        name={namePrefix + 'caption'}
                        defaultValue={caption}
                        ref={register()}
                        placeholder={placeholder}
                    />
                </Figures.Caption>
            </Figures.Figure>
            <input type="hidden" name={namePrefix + 'id'} value={id} ref={register()} />
            <input type="hidden" name={namePrefix + 'url'} value={url} ref={register()} />
        </section>
    )
}

function Actions({ index, lastIndex }) {
    const first = index === 0
    const last = index === lastIndex
    const intl = useIntl()
    const [state, send] = useService()
    const swapWithPrevious = React.useCallback(() => {
        send('MOVE_PREVIOUS', { index })
    }, [send, index])
    const swapWithNext = React.useCallback(() => {
        send('MOVE_NEXT', { index })
    }, [send, index])
    const remove = React.useCallback(() => {
        send('REMOVE', { index })
    }, [send, index])
    const previousTooltip = intl.formatMessage({
        description: 'MIN Form / Image Upload Widget',
        defaultMessage: 'Swap with previous image',
    })
    const nextTooltip = intl.formatMessage({
        description: 'MIN Form / Image Upload Widget',
        defaultMessage: 'Swap with next image',
    })
    const removeTooltip = intl.formatMessage({
        description: 'MIN Form / Image Upload Widget',
        defaultMessage: 'Remove image',
    })

    return (
        <div className={styles.Actions}>
            {first || (
                <Buttons.Button
                    disabled={!state.nextEvents.includes('MOVE_PREVIOUS')}
                    onClick={swapWithPrevious}
                    data-tooltip={previousTooltip}>
                    <Icons.ChevronLeft color={Colors.WHITE} />
                </Buttons.Button>
            )}
            {last || (
                <Buttons.Button
                    disabled={!state.nextEvents.includes('MOVE_NEXT')}
                    onClick={swapWithNext}
                    data-tooltip={nextTooltip}>
                    <Icons.ChevronRight color={Colors.WHITE} />
                </Buttons.Button>
            )}
            <Buttons.Button
                disabled={!state.nextEvents.includes('REMOVE')}
                onClick={remove}
                data-tooltip={removeTooltip}>
                <Icons.Remove color={Colors.WHITE} />
            </Buttons.Button>
        </div>
    )
}

function AddImage() {
    const { options } = useContext()
    const [state, send] = useService()
    const disabled = state.matches({ post: 'pending' })
    const className = clsx(styles.AddImage, {
        [styles.AddImageDisabled]: disabled,
    })
    const handleFileChange = React.useCallback(
        event => {
            const { files } = event.target

            send('ADD', { files })
        },
        [send]
    )

    return (
        <label className={className}>
            {disabled ? (
                <React.Fragment>
                    <div>
                        <FormattedMessage
                            description="MIN Form / Image Upload Widget"
                            defaultMessage="Adding images..."
                        />
                    </div>
                    <progress />
                </React.Fragment>
            ) : (
                <Icons.Add width="4em" height="4em" />
            )}
            <input
                {...options}
                disabled={disabled}
                type="file"
                hidden
                onChange={handleFileChange}
            />
        </label>
    )
}

function DropZone() {
    const [count, setCount] = React.useState(0)
    const [state, send] = useService()
    const { options } = useContext()
    const { multiple, accept } = options
    const [inside, enter, exit] = Hooks.useBoolean(false)
    const getImagesFromEvent = React.useCallback(
        event => {
            // Better to use items instead of files, it works for every event types (drop & drag)
            let images = Array.from(event.dataTransfer.items)
                .filter(item => item.kind === 'file')
                .filter(item => item.type.startsWith('image/'))

            if (Array.isArray(accept)) {
                const types = new Set(accept)

                images = images.filter(file => types.has(file.type))
            }

            images = images.map(item => item.getAsFile())

            if (multiple) {
                return images
            }

            return [images[0]]
        },
        [accept, multiple]
    )
    const dropHandler = React.useCallback(
        event => {
            const files = getImagesFromEvent(event)

            stopEvent(event)
            exit()
            send('ADD', { files })
        },
        [getImagesFromEvent, send, exit]
    )
    const dragEnterHandler = React.useCallback(
        event => {
            stopEvent(event)
            enter()
        },
        [enter]
    )
    const dragExitHandler = React.useCallback(
        event => {
            stopEvent(event)
            exit()
        },
        [exit]
    )
    const dragOverHandler = React.useCallback(
        event => {
            const { length } = getImagesFromEvent(event)

            stopEvent(event)
            setCount(length)
        },
        [getImagesFromEvent]
    )

    Hooks.useEventListener('drop', dropHandler, document)
    Hooks.useEventListener('dragover', dragOverHandler, document)
    Hooks.useEventListener('dragenter', dragEnterHandler, document)
    Hooks.useEventListener('dragexit', dragExitHandler, document)

    if (!inside || !state.matches('idle')) {
        return null
    }

    return (
        <div className={styles.DropZoneOverlay}>
            <FormattedMessage
                description="Drop Zone text"
                defaultMessage="{count, plural, 
                    =0 {Only images can be uploaded!} 
                    =1 {You can now drop your image...} 
                    other {You can now drop your # images...}
                }"
                values={{ count }}
            />
            <Icons.Download hidden={count === 0} color={Colors.WHITE} width={50} height={50} />
        </div>
    )
}

// Utils
function stopEvent(event) {
    event.stopPropagation()
    event.preventDefault()
}

// Context
function Provider({ name, children, register, options = OBJECT }) {
    const postImages = SubmissionClient.usePostImages()
    const { fields, append, remove, move } = useFieldArray({ name })
    const machine = XState.useMachine(Machine, {
        actions: {
            append(context, event) {
                const { id, url } = event.image

                append({ id, url })
            },
            remove(context, event) {
                remove(event.index)
            },
            movePrevious(context, event) {
                const { index } = event

                if (index === 0) {
                    return
                }

                move(index, index - 1)
            },
            moveNext(context, event) {
                const { index } = event

                if (index === fields.length - 1) {
                    return
                }

                move(index, index + 1)
            },
        },
        services: {
            postImages(context) {
                const images = Array.from(context.files.keys())

                return postImages(images)
            },
        },
    })
    const service = machine[2]
    const value = React.useMemo(() => {
        return {
            name,
            register,
            options,
            fields,
            service,
        }
    }, [options, name, register, fields, service])

    return <Context.Provider value={value}>{children}</Context.Provider>
}

function useService() {
    const { service } = useContext()

    return XState.useService(service)
}

function useContext() {
    return React.useContext(Context)
}

const Context = React.createContext()
const OBJECT = {}
